import { invertMatrix, Matrix2D, Point, transformPoint, XY } from '@imagise/shared';
import { Canvas } from '../core';
import type { ObjectDragEventDetail } from '../core';
import { copyCanvasElement, createCanvasElement, toBlob as canvasToBlob, toDataURL as canvasToDataURL } from '../util/misc/dom';
import type { TSize } from '../typedefs';
import { ArrangeImageTile, ArrangeTileSurface } from './arrange-image-tile';
import { EditorRasterImage } from './raster-image';
import { RasterImageLayer } from './raster-image-layer';
import type {
  EditorCanvasExportOptions,
  EditorCanvasOperationContext,
  EditorCanvasOperationHooks,
  EditorCanvasSnapshotOptions,
  EditorRasterLoadOptions,
  EditorRasterSource,
  FitImageToViewportOptions,
  ViewportFitResult,
} from './types';

export interface EditorArrangeTileInput {
  id: string;
  surface: ArrangeTileSurface;
  x: number;
  y: number;
  borderWidth?: number;
  borderColor?: string | null;
}

type ActiveEditorOperation = {
  hooks: EditorCanvasOperationHooks;
  baseline: HTMLCanvasElement;
};

const DEFAULT_EXPORT_FORMAT = 'png' as const;
const DEFAULT_EXPORT_QUALITY = 1;

/**
 * Arrange tiles live in result-image coordinates: negative positions would be
 * clipped away by the merge backend, and the upper bound keeps a stray drag
 * from inflating the result image beyond any sane size.
 */
export const MAX_ARRANGE_TILE_POSITION = 20000;

const toPoint = (point: Point | XY): Point => (point instanceof Point ? point.clone() : new Point(point));

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const createBlankCanvas = (size: Partial<TSize>): HTMLCanvasElement => {
  const canvas = createCanvasElement();
  canvas.width = Math.max(Math.round(size.width || 0), 1);
  canvas.height = Math.max(Math.round(size.height || 0), 1);
  return canvas;
};

const dataUrlToBlob = (dataUrl: string): Blob | null => {
  const [meta, content] = dataUrl.split(',');
  if (!meta || typeof content !== 'string') {
    return null;
  }
  const mimeMatch = meta.match(/data:([^;]+);base64/);
  const mimeType = mimeMatch?.[1] || 'application/octet-stream';

  if (typeof Uint8Array === 'undefined') {
    return null;
  }

  if (typeof atob === 'function') {
    const bytes = atob(content);
    const buffer = new Uint8Array(bytes.length);
    for (let index = 0; index < bytes.length; index += 1) {
      buffer[index] = bytes.charCodeAt(index);
    }
    return new Blob([buffer], { type: mimeType });
  }

  const runtimeBuffer = (globalThis as {
    Buffer?: {
      from(input: string, encoding: string): Uint8Array;
    };
  }).Buffer;
  if (runtimeBuffer) {
    const bytes = Uint8Array.from(runtimeBuffer.from(content, 'base64'));
    return new Blob([bytes.buffer], { type: mimeType });
  }

  return null;
};

export class EditorCanvas extends Canvas {
  /** Fired when the user finishes dragging an arrange tile. */
  onArrangeTileMoved?: (id: string, x: number, y: number) => void;

  private rasterLayer?: RasterImageLayer;
  private activeOperation?: ActiveEditorOperation;
  private readonly arrangeTilesById = new Map<string, ArrangeImageTile>();
  private arrangeEventsAttached = false;

  async loadRasterSource(
    source: EditorRasterSource,
    options: EditorRasterLoadOptions = {},
  ): Promise<RasterImageLayer> {
    const image = await EditorRasterImage.fromSource(source, options);
    return this.loadRasterImage(image);
  }

  loadRasterImage(image: EditorRasterImage): RasterImageLayer {
    const layer = RasterImageLayer.fromRasterImage(image);
    this.replaceRasterLayer(layer);
    return layer;
  }

  getRasterLayer(): RasterImageLayer | undefined {
    return this.rasterLayer;
  }

  getCurrentImageSize(): TSize | undefined {
    return this.rasterLayer?.getCurrentSize();
  }

  fitImageToViewport(options: FitImageToViewportOptions = {}): ViewportFitResult | undefined {
    const imageSize = this.getCurrentImageSize();
    if (!imageSize) {
      return undefined;
    }

    return this.fitAreaToViewport(imageSize, options);
  }

  fitAreaToViewport(imageSize: TSize, options: FitImageToViewportOptions = {}): ViewportFitResult {
    const padding = Math.max(options.padding || 0, 0);
    const viewportSize = {
      width: Math.max(this.width, 0),
      height: Math.max(this.height, 0),
    };
    const availableWidth = Math.max(viewportSize.width - padding * 2, 0);
    const availableHeight = Math.max(viewportSize.height - padding * 2, 0);
    const widthScale = availableWidth > 0 ? availableWidth / imageSize.width : 1;
    const heightScale = availableHeight > 0 ? availableHeight / imageSize.height : 1;
    const rawScale = Math.min(widthScale, heightScale);
    const scale = clamp(
      Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1,
      options.minScale ?? 0,
      options.maxScale ?? Number.POSITIVE_INFINITY,
    );
    const align = options.align ?? 'center';
    const translateX = align === 'start' ? padding : padding + (availableWidth - imageSize.width * scale) / 2;
    const translateY = align === 'start' ? padding : padding + (availableHeight - imageSize.height * scale) / 2;

    this.setViewportTransform([scale, 0, 0, scale, translateX, translateY]);

    return {
      scale,
      translateX,
      translateY,
      viewportSize,
      imageSize,
    };
  }

  screenToImage(point: Point | XY): Point | undefined {
    const layer = this.rasterLayer;
    if (!layer) {
      return undefined;
    }
    const scenePoint = transformPoint(toPoint(point), invertMatrix(this.viewportTransform as Matrix2D));
    return this.sceneToImagePoint(scenePoint, layer);
  }

  imageToScreen(point: Point | XY): Point | undefined {
    const layer = this.rasterLayer;
    if (!layer) {
      return undefined;
    }
    const scenePoint = this.imageToScenePoint(toPoint(point), layer);
    return transformPoint(scenePoint, this.viewportTransform as Matrix2D);
  }

  snapshot(options: EditorCanvasSnapshotOptions = {}): HTMLCanvasElement {
    const target = options.target ?? 'image';
    return target === 'viewport'
      ? this.snapshotViewport()
      : this.snapshotImage(options.includeDraft ?? true);
  }

  exportToDataURL(options: EditorCanvasExportOptions = {}): string {
    const canvas = this.snapshot(options);
    return canvasToDataURL(
      canvas,
      options.format ?? DEFAULT_EXPORT_FORMAT,
      options.quality ?? DEFAULT_EXPORT_QUALITY,
    );
  }

  async exportToBlob(options: EditorCanvasExportOptions = {}): Promise<Blob | null> {
    const canvas = this.snapshot(options);
    const format = options.format ?? DEFAULT_EXPORT_FORMAT;
    const quality = options.quality ?? DEFAULT_EXPORT_QUALITY;
    const blob = await canvasToBlob(canvas, format, quality);
    if (blob) {
      return blob;
    }
    return dataUrlToBlob(canvasToDataURL(canvas, format, quality));
  }

  async draft(operation: EditorCanvasOperationHooks): Promise<void> {
    const layer = this.requireRasterLayer();
    if (this.activeOperation) {
      await this.rollbackDraft();
    }

    this.activeOperation = {
      hooks: operation,
      baseline: layer.snapshot(false),
    };

    await operation.draft?.(this.createOperationContext(layer));
    this.requestRenderAll('manual');
  }

  async applyDraft(): Promise<void> {
    const layer = this.rasterLayer;
    const activeOperation = this.activeOperation;
    if (!layer || !activeOperation) {
      return;
    }

    await activeOperation.hooks.apply?.(this.createOperationContext(layer));
    layer.applyDraft();
    this.activeOperation = undefined;
    this.requestRenderAll('manual');
  }

  async rollbackDraft(): Promise<void> {
    const layer = this.rasterLayer;
    const activeOperation = this.activeOperation;
    if (!layer || !activeOperation) {
      return;
    }

    await activeOperation.hooks.rollback?.(this.createOperationContext(layer));
    layer.restoreAppliedSurface(activeOperation.baseline);
    this.activeOperation = undefined;
    this.requestRenderAll('manual');
  }

  async reset(): Promise<void> {
    const layer = this.rasterLayer;
    if (!layer) {
      return;
    }

    if (this.activeOperation) {
      await this.activeOperation.hooks.reset?.(this.createOperationContext(layer));
    }

    layer.reset();
    this.activeOperation = undefined;
    this.requestRenderAll('manual');
  }

  clearRasterLayer(): void {
    if (!this.rasterLayer) {
      return;
    }

    this.remove(this.rasterLayer);
    this.rasterLayer = undefined;
    this.activeOperation = undefined;
    this.requestRenderAll('manual');
  }

  /**
   * Shows the given set of draggable tiles, diffing against the current set:
   * existing tiles keep their identity (and an in-flight drag), new ones are
   * added, missing ones removed. The array order defines the draw order —
   * later tiles render on top.
   */
  setArrangeTiles(inputs: readonly EditorArrangeTileInput[]): void {
    this.attachArrangeEvents();

    const seenIds = new Set<string>();
    inputs.forEach(input => {
      seenIds.add(input.id);
      let tile = this.arrangeTilesById.get(input.id);

      if (!tile) {
        tile = new ArrangeImageTile(input.id, input.surface, {
          x: input.x,
          y: input.y,
        });
        this.arrangeTilesById.set(input.id, tile);
        this.add(tile);
      } else {
        tile.setSurface(input.surface);
        // Never fight an active drag: the pointer owns the position until release.
        if (!tile.isMoving && (tile.left !== input.x || tile.top !== input.y)) {
          tile.left = input.x;
          tile.top = input.y;
          tile.setCoords();
        }
      }

      tile.setTileStyle(input.borderWidth ?? 0, input.borderColor ?? null);
    });

    for (const [id, tile] of Array.from(this.arrangeTilesById)) {
      if (!seenIds.has(id)) {
        this.remove(tile);
        this.arrangeTilesById.delete(id);
      }
    }

    // Keep the stack order in sync with the input order (raster layer stays below).
    const baseIndex = this.rasterLayer ? this.objects.indexOf(this.rasterLayer) + 1 : 0;
    inputs.forEach((input, index) => {
      const tile = this.arrangeTilesById.get(input.id);
      if (tile) {
        this.moveObjectTo(tile, baseIndex + index);
      }
    });

    this.requestRenderAll('manual');
  }

  clearArrangeTiles(): void {
    if (!this.arrangeTilesById.size) {
      return;
    }

    this.discardActiveObject();
    for (const tile of this.arrangeTilesById.values()) {
      this.remove(tile);
    }
    this.arrangeTilesById.clear();
    this.requestRenderAll('manual');
  }

  get hasArrangeTiles(): boolean {
    return this.arrangeTilesById.size > 0;
  }

  private attachArrangeEvents(): void {
    if (this.arrangeEventsAttached) {
      return;
    }

    this.arrangeEventsAttached = true;
    this.on('object:moving', detail => {
      const { target } = detail as ObjectDragEventDetail;
      if (target instanceof ArrangeImageTile) {
        this.clampArrangeTilePosition(target);
      }
    });
    this.on('object:modified', detail => {
      const { target } = detail as ObjectDragEventDetail;
      if (!(target instanceof ArrangeImageTile) || !target.id) {
        return;
      }

      this.clampArrangeTilePosition(target);
      this.onArrangeTileMoved?.(target.id, target.left, target.top);
    });
  }

  private clampArrangeTilePosition(tile: ArrangeImageTile): void {
    const left = clamp(tile.left, 0, MAX_ARRANGE_TILE_POSITION);
    const top = clamp(tile.top, 0, MAX_ARRANGE_TILE_POSITION);

    if (left !== tile.left || top !== tile.top) {
      tile.left = left;
      tile.top = top;
      tile.setCoords();
    }
  }

  private replaceRasterLayer(layer: RasterImageLayer): void {
    if (this.rasterLayer) {
      this.remove(this.rasterLayer);
    }
    this.activeOperation = undefined;
    this.rasterLayer = layer;
    this.insertAt(0, layer);
    this.requestRenderAll('manual');
  }

  private requireRasterLayer(): RasterImageLayer {
    if (!this.rasterLayer) {
      throw new Error('EditorCanvas: raster layer is not loaded');
    }
    return this.rasterLayer;
  }

  private snapshotImage(includeDraft: boolean): HTMLCanvasElement {
    return this.rasterLayer?.snapshot(includeDraft) ?? createBlankCanvas({ width: this.width, height: this.height });
  }

  private snapshotViewport(): HTMLCanvasElement {
    this.renderAll();
    const lowerCanvas = this.lowerCanvasEl ?? this.getElement();
    return lowerCanvas ? copyCanvasElement(lowerCanvas) : createBlankCanvas({ width: this.width, height: this.height });
  }

  private sceneToImagePoint(point: Point, layer: RasterImageLayer): Point {
    const centeredPoint = transformPoint(point, invertMatrix(layer.calcTransformMatrix() as Matrix2D));
    return new Point(centeredPoint.x + layer.width / 2, centeredPoint.y + layer.height / 2);
  }

  private imageToScenePoint(point: Point, layer: RasterImageLayer): Point {
    const centeredPoint = new Point(point.x - layer.width / 2, point.y - layer.height / 2);
    return transformPoint(centeredPoint, layer.calcTransformMatrix() as Matrix2D);
  }

  private createOperationContext(layer: RasterImageLayer): EditorCanvasOperationContext {
    return {
      canvas: this,
      rasterLayer: layer,
      imageSize: layer.getCurrentSize(),
      viewportSize: {
        width: this.width,
        height: this.height,
      },
      screenToImage: point => this.screenToImage(point),
      imageToScreen: point => this.imageToScreen(point),
    };
  }
}
